/**
 * Calendar-date keys for the Travel Desk's pure logic (ADR 0030, plan §5.7). Pure and RN-safe.
 *
 * A trip's dates are local calendar days — `YYYY-MM-DD` in the traveller's `location.timezone`
 * (the entity's when the location has none, EC-50). The caller resolves "today" in that zone and
 * passes it in; nothing here reads a clock or the server's timezone (CLAUDE.md invariant 3).
 *
 * Keys are compared as strings (zero-padded ISO dates sort correctly) and stepped through UTC
 * midnight, which has no DST, so a day is always exactly one day.
 */

const DAY_MS = 86_400_000;
const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real calendar date written `YYYY-MM-DD` (rejects `2026-02-30`). */
export function isDateKey(value: string): boolean {
  const match = DATE_KEY.exec(value);
  if (!match) return false;
  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(ms).toISOString().slice(0, 10) === value;
}

/** UTC midnight of a key, in ms. Throws on a malformed key — a programming error, not input. */
function toMs(key: string): number {
  if (!isDateKey(key)) throw new RangeError(`Invalid date key: ${key}`);
  return Date.parse(`${key}T00:00:00.000Z`);
}

function fromMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** `key` moved by `days` (negative goes back). */
export function addDays(key: string, days: number): string {
  return fromMs(toMs(key) + days * DAY_MS);
}

/** Whole days from `from` to `to`: `diffDays('2026-10-01', '2026-10-03') === 2`. */
export function diffDays(from: string, to: string): number {
  return Math.round((toMs(to) - toMs(from)) / DAY_MS);
}

/** -1, 0 or 1, for `Array.prototype.sort`. */
export function compareDateKeys(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Every day from `start` to `end`, both inclusive; empty when `end` is before `start`. */
export function eachDay(start: string, end: string): string[] {
  const days: string[] = [];
  const last = toMs(end);
  for (let ms = toMs(start); ms <= last; ms += DAY_MS) days.push(fromMs(ms));
  return days;
}

/** `start <= key <= end`, all inclusive. */
export function isWithin(key: string, start: string, end: string): boolean {
  return key >= start && key <= end;
}

/** 0 = Sunday, as `BusinessCalendar.weeklyOffDays` counts. */
export function weekdayOf(key: string): number {
  return new Date(toMs(key)).getUTCDay();
}

/** `HH:MM` (24-hour) to minutes since midnight, or null when missing or unparseable. */
export function clockMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}
